// packages/core/src/diff/lookup.ts
import type {
  ChangeSet,
  DiffLookup,
  EntityRef,
  SurfaceDiff,
  ModuleDiff,
  Status,
} from "../diff-types.js";

export function buildLookup(cs: ChangeSet): DiffLookup {
  const pathStatus = new Map<string, Status>();

  function walk(mod: ModuleDiff, prefix: string) {
    pathStatus.set(prefix, mod.status);
    for (const [k, v] of mod.fields) pathStatus.set(`${prefix}.${k}`, v.status);
    for (const [k, v] of mod.aspects) pathStatus.set(`${prefix}.aspects.${k}`, v.status);
    for (const [k, v] of mod.interfaces) {
      pathStatus.set(`${prefix}.exports.${k}`, v.status);
      for (const [fk, fv] of v.fields) pathStatus.set(`${prefix}.exports.${k}.${fk}`, fv.status);
    }
    for (const [, surface] of mod.surfaces) walkSurface(surface, `${prefix}.surfaces.${surface.name}`);
    for (const [, child] of mod.children) walk(child, `${prefix}.${child.name}`);
  }

  function walkSurface(f: SurfaceDiff, prefix: string) {
    pathStatus.set(prefix, f.status);
    for (const [k, v] of f.fields) pathStatus.set(`${prefix}.${k}`, v.status);
    for (const [k, v] of f.aspects) pathStatus.set(`${prefix}.aspects.${k}`, v.status);
    for (const [k, v] of f.interfaces) {
      pathStatus.set(`${prefix}.${k}`, v.status);
      for (const [fk, fv] of v.fields) pathStatus.set(`${prefix}.${k}.${fk}`, fv.status);
    }
    for (const [, sub] of f.surfaces) walkSurface(sub, `${prefix}.${sub.name}`);
  }

  for (const [id, m] of cs.modules) walk(m, String(id));
  for (const [id, p] of cs.processes) pathStatus.set(String(id), p.status);
  for (const [id, sp] of cs.subprocesses) pathStatus.set(String(id), sp.status);
  for (const [id, v] of cs.views) pathStatus.set(String(id), v.status);

  function entityStatus(ref: EntityRef): Status {
    if (ref.kind === "module") return cs.modules.get(ref.id)?.status ?? "unchanged";
    if (ref.kind === "process") return cs.processes.get(ref.id)?.status ?? "unchanged";
    if (ref.kind === "subprocess") return cs.subprocesses.get(ref.id)?.status ?? "unchanged";
    if (ref.kind === "view") return cs.views.get(ref.id)?.status ?? "unchanged";
    return cs.modules.get(ref.moduleId)?.interfaces.get(ref.name)?.status ?? "unchanged";
  }
  function statusOf(path: string): Status {
    return pathStatus.get(path) ?? "unchanged";
  }
  function changedPaths(scope: EntityRef): readonly string[] {
    if (scope.kind !== "module") return [];
    const prefix = String(scope.id);
    return [...pathStatus.entries()]
      .filter(([k, v]) => v !== "unchanged" && (k === prefix || k.startsWith(prefix + ".")))
      .map(([k]) => k);
  }
  return { entityStatus, statusOf, changedPaths };
}
